import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Menu, MenuItem } from '@mui/material';
import { UiTooltip, UiIcon } from 'ui-components';
import Fade from '@mui/material/Fade';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UiTableDeleteOptionModal from 'ui-components/ui-table/ui-table-delete-option-modal/UiTableDeleteOptionModal';
import { styled, alpha } from '@mui/material/styles';
import { StyledUiMenu } from './UiMenu.styles';

const StyledMenu = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'left',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'left',
    }}
    autoFocus={false}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 8,
    marginTop: theme.spacing(1),
    minWidth: 230,
    boxShadow:
      '0px 3px 8px -1px rgba(50, 50, 71, 0.05),0px 0px 1px rgba(12, 26, 75, 0.24)',

    '& .MuiMenu-list': {
      padding: '4px 0',
    },
  },
  '& .MuiList-root': {
    maxHeight: 'unset !important',
    padding: '0 !important',
    margin: '0 !important',
  },
  '& .focusVisible': {
    backgroundColor: 'unset !important',
  },
  '& .MuiMenuItem-root': {
    fontFamily: 'inherit !important',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '0.875rem',
    padding: 0,
    color: 'rgb(78, 78, 78)',
    '&:active': {
      backgroundColor: alpha(
        theme.palette.primary.main,
        theme.palette.action.selectedOpacity
      ),
    },
    '& .icon': {
      paddingLeft: 8,
      color: 'rgb(110, 110, 110)',
    },
    '& > *': {
      width: '100%',
    },
    '& .info': {
      height: 48,
      minWidth: '100% !important',
      padding: '6px 16px',
      boxSizing: 'border-box',
    },
    '&:hover': {
      color: '#000000',
      '& .icon': {
        color: '#000000',
      },
    },
  },
}));

const UiMenu = (props) => {
  const {
    className,
    iconClassName,
    actionsList = [],
    updateData,
    data = {},
    forceClose,
    button,
    ...restProps
  } = props;
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const getActionClickHandler = (onClick, disabled) => {
    if (disabled || !onClick) {
      return undefined;
    }

    return (event) => {
      onClick(event);
      handleClose();
    };
  };

  useEffect(() => {
    if (forceClose > 0) handleClose();
  }, [forceClose]);

  return (
    <StyledUiMenu>
      <div>
        {!button && (
          <UiTooltip
            title={t('common:more')}
            enterDelay={500}
            enterNextDelay={500}
            placement='top'
            TransitionComponent={Fade}
          >
            <span
              id='mui-menu-button'
              className={classNames(iconClassName, 'flex')}
              onClick={handleClick}
              data-testid={`more-action-submit-icon-${props?.['data-testid']}`}
              aria-controls={open ? 'mui-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={open ? 'true' : undefined}
            >
              <UiIcon icon='ri-more-2-fill' className='more-actions-icon' />
            </span>
          </UiTooltip>
        )}
        {button && <div onClick={handleClick}>{button}</div>}
        <StyledMenu
          id='mui-menu'
          MenuListProps={{
            'aria-labelledby': 'mui-menu-button',
          }}
          className={className}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          data-testid={`more-action-list-${props?.['data-testid']}`}
          {...restProps}
        >
          {actionsList?.map(
            (
              {
                render,
                key,
                disabled,
                onClick,
                iconSrc,
                title,
                permission = true,
                route,
                ...rest
              },
              index
            ) => {
              const action = {
                render,
                key,
                disabled,
                onClick,
                iconSrc,
                title,
                permission,
                route,
                ...rest,
              };
              if (!permission) return null;
              if (render)
                return (
                  <MenuItem
                    disableRipple
                    key={key || index}
                    disabled={disabled}
                    focusVisibleClassName='focusVisible'
                    onClick={getActionClickHandler(onClick, disabled)}
                  >
                    {render(action, updateData, data)}
                  </MenuItem>
                );
              if (key === 'edit')
                return (
                  <MenuItem
                    disableRipple
                    key={key || index}
                    disabled={disabled}
                    focusVisibleClassName='focusVisible'
                  >
                    <Link
                      to={route?.replace(':id', data?.id)}
                      className='info flex'
                      disabled={disabled}
                      data-testid={`edit-action-${props?.['data-testid']}`}
                    >
                      <UiIcon icon={iconSrc} color='#FFFFFF60' />
                      <span>{title}</span>
                    </Link>
                  </MenuItem>
                );
              if (key === 'delete')
                return (
                  <MenuItem
                    disableRipple
                    key={key || index}
                    disabled={disabled}
                    focusVisibleClassName='focusVisible'
                  >
                    <UiTableDeleteOptionModal
                      className='info flex'
                      selected={[data]}
                      action={action}
                      updateData={updateData}
                      data-testid={`delete-action-${props?.['data-testid']}`}
                    >
                      <UiIcon icon={iconSrc} color='#FFFFFF60' />
                      <span>{title}</span>
                    </UiTableDeleteOptionModal>
                  </MenuItem>
                );
              if (onClick)
                return (
                  <MenuItem
                    disableRipple
                    key={key || index}
                    disabled={disabled}
                    focusVisibleClassName='focusVisible'
                  >
                    <span
                      className='info flex'
                      onClick={disabled ? null : onClick}
                      disabled={disabled}
                    >
                      <UiIcon icon={iconSrc} color='#FFFFFF60' />
                      <span>{title}</span>
                    </span>
                  </MenuItem>
                );
              return <></>;
            }
          )}
        </StyledMenu>
      </div>
    </StyledUiMenu>
  );
};

UiMenu.propTypes = {
  actionsList: PropTypes.array,
  updateData: PropTypes.func,
  data: PropTypes.object,
  className: PropTypes.string,
  iconClassName: PropTypes.string,
  forceClose: PropTypes.bool,
  'data-testid': PropTypes.string,
  button: PropTypes.bool,
};

export default UiMenu;
